import React, { useState } from 'react';
import { ContentData } from '../data/contentData';

interface AdmissionsPageProps {
  content: ContentData;
}

interface AdmissionForm {
  studentName: string;
  fatherName: string;
  motherName: string;
  dateOfBirth: string;
  gender: string;
  class: string;
  previousSchool: string;
  address: string;
  phone: string;
  email: string;
  guardianName: string;
  guardianPhone: string;
  guardianRelation: string;
}

const AdmissionsPage: React.FC<AdmissionsPageProps> = ({ content }) => {
  const [activeTab, setActiveTab] = useState('requirements');
  const [formData, setFormData] = useState<AdmissionForm>({
    studentName: '',
    fatherName: '',
    motherName: '',
    dateOfBirth: '',
    gender: '',
    class: '',
    previousSchool: '',
    address: '',
    phone: '',
    email: '',
    guardianName: '',
    guardianPhone: '',
    guardianRelation: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const admissionRequirements = [
    {
      level: 'প্রাথমিক শিক্ষা (১ম-৫ম শ্রেণি)',
      age: '৬-১০ বছর',
      documents: [
        'জন্ম সনদ (মূল ও ফটোকপি)',
        'পাসপোর্ট সাইজের ছবি ৩ কপি',
        'পূর্ববর্তী বিদ্যালয়ের সনদপত্র',
        'অভিভাবকের জাতীয় পরিচয়পত্র (ফটোকপি)',
        'মেডিকেল সার্টিফিকেট'
      ],
      fees: 'মাসিক ২,৫০০ টাকা',
      icon: '🎒'
    },
    {
      level: 'মাধ্যমিক শিক্ষা (৬ষ্ঠ-১০ম শ্রেণি)',
      age: '১১-১৫ বছর',
      documents: [
        'জন্ম সনদ (মূল ও ফটোকপি)',
        'পাসপোর্ট সাইজের ছবি ৩ কপি',
        'পূর্ববর্তী শ্রেণির পাস সনদ',
        'অভিভাবকের জাতীয় পরিচয়পত্র (ফটোকপি)',
        'মেডিকেল সার্টিফিকেট',
        'চরিত্র সনদ'
      ],
      fees: 'মাসিক ৩,৫০০ টাকা',
      icon: '📚'
    },
    {
      level: 'উচ্চ মাধ্যমিক (১১শ-১২শ শ্রেণি)',
      age: '১৬-১৭ বছর',
      documents: [
        'জন্ম সনদ (মূল ও ফটোকপি)',
        'পাসপোর্ট সাইজের ছবি ৩ কপি',
        'এস.এস.সি. সনদপত্র',
        'এস.এস.সি. মার্কশিট',
        'অভিভাবকের জাতীয় পরিচয়পত্র (ফটোকপি)',
        'মেডিকেল সার্টিফিকেট',
        'চরিত্র সনদ'
      ],
      fees: 'মাসিক ৪,৫০০ টাকা',
      icon: '🎓'
    }
  ];

  const tabs = [
    { id: 'requirements', label: 'প্রয়োজনীয়তা', icon: '📋' },
    { id: 'process', label: 'আবেদন প্রক্রিয়া', icon: '📝' },
    { id: 'fees', label: 'ফি কাঠামো', icon: '💰' },
    { id: 'apply', label: 'অনলাইন আবেদন', icon: '💻' }
  ];

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 bangla-font">{content.admissions.title}</h1>
          <p className="text-lg text-gray-600 bangla-font">ভবিষ্যতের জন্য সঠিক শিক্ষার সন্ধানে</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                <span className="bangla-font">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Admission Requirements */}
        {activeTab === 'requirements' && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center bangla-font">{content.admissions.requirements}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {admissionRequirements.map((req, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6">
                    <div className="text-5xl mb-4 text-center">{req.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center bangla-font">{req.level}</h3>
                    <p className="text-blue-600 font-medium mb-4 text-center bangla-font">বয়স: {req.age}</p>
                    <p className="text-green-600 font-medium mb-4 text-center bangla-font">বেতন: {req.fees}</p>
                    
                    <h4 className="font-semibold text-gray-900 mb-3 bangla-font">প্রয়োজনীয় কাগজপত্র:</h4>
                    <ul className="space-y-2">
                      {req.documents.map((doc, idx) => (
                        <li key={idx} className="text-sm text-gray-600 bangla-font flex items-start">
                          <span className="text-blue-500 mr-2">•</span>
                          {doc}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3 bangla-font">গুরুত্বপূর্ণ তথ্য:</h3>
              <ul className="space-y-2 text-yellow-700">
                <li className="bangla-font">• ভর্তি পরীক্ষা বাধ্যতামূলক</li>
                <li className="bangla-font">• সমস্ত কাগজপত্রের মূল কপি প্রয়োজন</li>
                <li className="bangla-font">• ভর্তি ফি এককালীন প্রদান করতে হবে</li>
                <li className="bangla-font">• ভর্তির সময় অভিভাবকের উপস্থিতি আবশ্যক</li>
              </ul>
            </div>
          </div>
        )}

        {/* Other tabs placeholder */}
        {activeTab === 'process' && (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center bangla-font">আবেদন প্রক্রিয়া</h2>
            <p className="text-center text-gray-600 bangla-font">আবেদন প্রক্রিয়ার বিস্তারিত তথ্য যোগ করা হচ্ছে...</p>
          </div>
        )}

        {activeTab === 'fees' && (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center bangla-font">ফি কাঠামো</h2>
            <p className="text-center text-gray-600 bangla-font">ফি কাঠামোর বিস্তারিত তথ্য যোগ করা হচ্ছে...</p>
          </div>
        )}

        {activeTab === 'apply' && (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center bangla-font">অনলাইন আবেদন</h2>
            <p className="text-center text-gray-600 bangla-font">অনলাইন আবেদন ফরম যোগ করা হচ্ছে...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdmissionsPage;
